/**
 * Sala de espera e admissão.
 *
 * Regra do PRD: ninguém entra sozinho. O paciente conecta, faz o teste de
 * câmera, microfone e fone, e fica esperando. A terapeuta admite. Só depois
 * disso a mídia começa a negociar, o que também evita gastar TURN com quem
 * ainda não foi aceito.
 */
import { z } from 'zod'

export const therapistPresenceSchema = z.enum(['absent', 'present'])

export const patientPresenceSchema = z.enum([
  /** Sem ninguém do lado do paciente. */
  'absent',
  /** Conectado, ainda fazendo o teste de equipamento. */
  'checking',
  /** Terminou o teste e pediu pra entrar. */
  'waiting',
  /** A terapeuta deixou entrar. */
  'admitted',
])

export const presenceSchema = z.object({
  therapist: therapistPresenceSchema,
  patient: patientPresenceSchema,
})

export type TherapistPresence = z.infer<typeof therapistPresenceSchema>
export type PatientPresence = z.infer<typeof patientPresenceSchema>
export type Presence = z.infer<typeof presenceSchema>

export const initialPresence: Presence = { therapist: 'absent', patient: 'absent' }

export type PresenceEvent =
  | { type: 'therapist-joined' }
  | { type: 'therapist-left' }
  | { type: 'patient-joined' }
  | { type: 'patient-left' }
  /** O paciente terminou o teste de equipamento e pediu pra entrar. */
  | { type: 'patient-ready' }
  | { type: 'admit' }
  /** A terapeuta devolveu o paciente pra sala de espera. */
  | { type: 'dismiss' }

export function applyPresence(presence: Presence, event: PresenceEvent): Presence {
  switch (event.type) {
    case 'therapist-joined':
      return { ...presence, therapist: 'present' }

    case 'therapist-left':
      // O paciente volta pra espera. Se a terapeuta reconectar, ela admite de
      // novo: é um clique, e é mais seguro do que deixar a sala aberta
      // sozinha com alguém dentro.
      return {
        therapist: 'absent',
        patient: presence.patient === 'admitted' ? 'waiting' : presence.patient,
      }

    case 'patient-joined':
      return { ...presence, patient: 'checking' }

    case 'patient-left':
      return { ...presence, patient: 'absent' }

    case 'patient-ready':
      if (presence.patient === 'absent') return presence
      // Já admitido continua admitido: um reload no meio da sessão não deve
      // jogar o paciente pra fila de novo.
      if (presence.patient === 'admitted') return presence
      return { ...presence, patient: 'waiting' }

    case 'admit':
      if (presence.patient !== 'waiting') return presence
      return { ...presence, patient: 'admitted' }

    case 'dismiss':
      if (presence.patient !== 'admitted') return presence
      return { ...presence, patient: 'waiting' }
  }
}

/** A mídia só negocia com os dois presentes e o paciente admitido. */
export function mediaAllowed(presence: Presence): boolean {
  return presence.therapist === 'present' && presence.patient === 'admitted'
}
